import type { PayRun } from '../../data/types';

/**
 * Pure display logic for a finished pay run: the completion view's heading,
 * the "Approved by … on …" line (shared by the completion view and the
 * Approval step), and the approval person cards. Nothing here ever produces
 * a placeholder dash for an unknown name or date — unknown values are
 * `undefined`, and the components omit the text instead.
 */

/** Heading for the completion view, by status. */
/** A trimmed, non-empty name, or `undefined` when it is unknown. */
export function known_name(name: string | undefined): string | undefined {
  const trimmed = name?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

/**
 * The approver's name to show: the audit feed's `actor_display_name` for
 * the run's approve entry first (the real backend's only source of a name),
 * then whatever the run itself carries (mock fixtures name their approver;
 * real runs carry `''`). `undefined` when neither knows.
 */
export function resolve_approver_name(
  audit_name: string | undefined,
  run: Pick<PayRun, 'approval'>,
): string | undefined {
  return known_name(audit_name) ?? known_name(run.approval.approver_name);
}

/** `true` when `at` parses to a real instant — `Intl` throws on an invalid date. */
function is_valid_instant(at: string): boolean {
  return !Number.isNaN(Date.parse(at));
}

export type ApprovalLine =
  | { kind: 'by_on'; name: string; approved_at: string }
  | { kind: 'on'; approved_at: string };

/**
 * Which "approved" sentence to print, or `undefined` for none: with a name
 * and a date, "Approved by {name} on {date}"; with only a date, "Approved
 * on {date}". Without a usable date there is no line at all — a name alone
 * is already on the approver card, and a guessed date would be worse than
 * none.
 */
export function approval_line(
  approved_at: string | null | undefined,
  approver_name: string | undefined,
): ApprovalLine | undefined {
  if (
    approved_at === null ||
    approved_at === undefined ||
    !is_valid_instant(approved_at)
  ) {
    return undefined;
  }
  const name = known_name(approver_name);
  return name === undefined
    ? { kind: 'on', approved_at }
    : { kind: 'by_on', name, approved_at };
}

export type PartyLabel =
  | { kind: 'name_title'; name: string; title: string }
  | { kind: 'name'; name: string };

/**
 * The name line on an approval person card: "{name}, {title}" when both are
 * known, just the name when the title is not, and `undefined` (the line is
 * hidden; the status pill stays) when the name itself is unknown.
 */
export function party_label(
  name: string,
  title?: string,
): PartyLabel | undefined {
  const known = known_name(name);
  if (known === undefined) return undefined;
  const known_title = known_name(title);
  return known_title === undefined
    ? { kind: 'name', name: known }
    : { kind: 'name_title', name: known, title: known_title };
}
